import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import {
  LINEAR_CATEGORIES,
  TEMPERATURE_UNITS,
  convertTemperature,
} from './utils/unitCategories'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const CATEGORY_OPTIONS = [
  ...Object.entries(LINEAR_CATEGORIES).map(([key, cat]) => ({
    key,
    label: cat.label,
  })),
  { key: 'temperatura', label: 'Temperatura' },
]

const unitsForCategory = (category) =>
  category === 'temperatura'
    ? TEMPERATURE_UNITS
    : Object.fromEntries(
      Object.entries(LINEAR_CATEGORIES[category].units).map(([k, u]) => [
        k,
        u.label,
      ]),
    )

const UnitConverter = () => {
  useDocumentMeta({
    title: 'Conversor de Unidades Online  Comprimento, Peso e Temperatura',
    description:
      'Converta km para milhas, kg para libras, Celsius para Fahrenheit e ' +
      'mais  rápido, grátis e direto no navegador, sem cadastro.',
  })

  const [category, setCategory] = useState('comprimento')
  const [fromUnit, setFromUnit] = useState('km')
  const [toUnit, setToUnit] = useState('mi')
  const [value, setValue] = useState('1')

  const units = unitsForCategory(category)

  const handleCategoryChange = (nextCategory) => {
    const nextUnits = Object.keys(unitsForCategory(nextCategory))
    setCategory(nextCategory)
    setFromUnit(nextUnits[0])
    setToUnit(nextUnits[1] || nextUnits[0])
    trackEvent('tool_convert_category', { category: nextCategory })
  }

  const result = useMemo(() => {
    const num = parseFloat(String(value).replace(',', '.'))
    if (Number.isNaN(num)) return ''

    if (category === 'temperatura') {
      return convertTemperature(num, fromUnit, toUnit).toFixed(2)
    }

    const catUnits = LINEAR_CATEGORIES[category].units
    const inBase = num * catUnits[fromUnit].toBase
    return (inBase / catUnits[toUnit].toBase).toFixed(4)
  }, [value, fromUnit, toUnit, category])

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Conversor de Unidades</h1>

      <div className="tool-field">
        <label htmlFor="category">Categoria</label>
        <select
          id="category"
          value={category}
          onChange={(e) => handleCategoryChange(e.target.value)}
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="value">Valor</label>
          <input
            id="value"
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="fromUnit">De</label>
          <select
            id="fromUnit"
            value={fromUnit}
            onChange={(e) => setFromUnit(e.target.value)}
          >
            {Object.entries(units).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="tool-field">
          <label htmlFor="toUnit">Para</label>
          <select
            id="toUnit"
            value={toUnit}
            onChange={(e) => setToUnit(e.target.value)}
          >
            {Object.entries(units).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tool-result-card">
        <span className="tool-result-label">Resultado</span>
        <span className="tool-result-value">
          {result === '' ? '' : result}
        </span>
      </div>

      <div className="tool-info-section">
        <h2>Sobre as categorias</h2>
        <p>
          Comprimento, peso e volume são convertidos por um fator de
          multiplicação direto entre as unidades. Temperatura é diferente:
          Celsius, Fahrenheit e Kelvin têm escalas com pontos de partida
          distintos, então a conversão exige fórmulas próprias (como °F =
          °C × 9/5 + 32) em vez de uma simples multiplicação.
        </p>
      </div>
    </div>
  )
}

export default UnitConverter
